import { useCallback, useEffect, useRef, useState } from "react";
import { safeBottom } from "./pwa";

/* 悬浮按钮共用：自由拖动（记住位置）· 拖到左右边缘或点 × → 贴边隐藏为窄条 · 鼠标移到窄条 / 点击窄条 → 弹出 · 移开后自动缩回；面板打开时保持显示。
 *
 * 2026-10-18 修复小屏缩放后停留位置错误：
 * 原实现只保存按钮的绝对 top/left，并在窗口 resize 时把位置夹回视口。
 * 小屏时两个按钮会被同时挤到右下角并互相避让；恢复大屏后坐标仍停在旧位置，
 * 且先挂载的 AI 按钮去重时另一个按钮还没挂载，后挂载的沟通按钮又只按自己的存储坐标避让，
 * 最终两个按钮重叠。这里把位置持久化为相对「右下锚点」的偏移，并在视口变化时统一重新约束；
 * 这样浏览器缩放、手机横竖屏切换和跨设备记忆都会保持按钮与视口边缘的相对关系。
 */
export const FAB_SIZE = 56;
const EDGE = 8;
const DOCK_ZONE = 28;
const RETRACT_MS = 700;
const HOVER_GRACE_MS = 1200;
const TOUCH_PEEK_MS = 5000;
const FAB_GAP = 8;
const POSITION_VERSION = 2;
const VIEWPORT_EVENT = "ali:floating-fab-viewport";

// 手机新增底部导航后，悬浮按钮的拖动范围须同步缩小，避免旧的记忆位置遮住导航。
const availableHeight = () => window.innerHeight - safeBottom() - (window.innerWidth < 768 ? 72 : 0);

const load = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k) || "null"); return v ?? d; } catch { return d; } };
const save = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };
const viewport = () => ({
  W: Number.isFinite(window.innerWidth) ? window.innerWidth : 0,
  // 手机新增底部导航（64px + 安全区）后，悬浮球可拖范围同步缩小，
  // 否则从桌面记忆下来的位置会落在导航条底下，用户点不到悬浮球。
  H: Number.isFinite(window.innerHeight) ? window.innerHeight - safeBottom() - (window.innerWidth < 768 ? 72 : 0) : 0,
});
const maxX = () => Math.max(EDGE, viewport().W - FAB_SIZE - EDGE);
const maxY = () => Math.max(EDGE, viewport().H - FAB_SIZE - EDGE);
const clamp = (p) => {
  const x = Number.isFinite(p?.x) ? p.x : maxX();
  const y = Number.isFinite(p?.y) ? p.y : maxY();
  return { x: Math.min(Math.max(EDGE, x), maxX()), y: Math.min(Math.max(EDGE, y), maxY()) };
};
const sideOf = (p) => (p.x <= EDGE + DOCK_ZONE ? "left" : p.x >= window.innerWidth - FAB_SIZE - EDGE - DOCK_ZONE ? "right" : "");
const edgeX = (side) => (side === "left" ? EDGE : window.innerWidth - FAB_SIZE - EDGE);
const posKey = (key) => `${key}_pos_v${POSITION_VERSION}`;

/**
 * 把按钮坐标转换成相对右下角的偏移。
 *
 * 之所以不保存绝对像素：浏览器缩放会改变 CSS 视口宽度，手机旋转会交换宽高，
 * 绝对坐标在换尺寸后看起来就会“粘”在页面中间。相对偏移可让贴右下的按钮继续贴右下，
 * 用户拖到左侧或中间时也仍能保留与右边缘的距离；视口变小时再统一夹回可见区。
 */
const toRelative = (p) => {
  const { W, H } = viewport();
  return { right: Math.max(0, W - FAB_SIZE - p.x), bottom: Math.max(0, H - FAB_SIZE - p.y) };
};

/** 将右下偏移还原成当前视口坐标；兼容旧版绝对坐标，避免已有用户升级后位置丢失。 */
const fromSaved = (saved) => {
  if (!saved || typeof saved !== "object") return null;
  if (typeof saved.x === "number" && typeof saved.y === "number") return { x: saved.x, y: saved.y };
  if (typeof saved.right !== "number" || typeof saved.bottom !== "number") return null;
  const { W, H } = viewport();
  return { x: W - FAB_SIZE - saved.right, y: H - FAB_SIZE - saved.bottom };
};

/** 读取某个悬浮球当前真实位置；拖动过程中优先使用内存值，避免 localStorage 尚未来得及写入。 */
const readPos = (key, current) => {
  if (current) return current;
  return fromSaved(load(posKey(key), null)) || fromSaved(load(`${key}_pos`, null));
};

/**
 * 两个悬浮球共享同一套避让规则。
 *
 * 2026-10-18 修复：原逻辑只在每个球初始化时检查一次 avoid，两个 React effect 的执行顺序
 * 会让后挂载的球看不到先挂载球的最新位置。现在视口变化或任一球移动后都会广播事件，
 * 两个球基于当前坐标重新排布：默认沟通球在右下，AI 球在它上方；空间不足时再改为左右错开。
 * 规则保持确定性，避免两个球互相追着移动。
 */
const resolvePositions = (entries) => {
  const rightEdge = maxX();
  const bottomEdge = maxY();
  const result = new Map();
  const ordered = [...entries].sort((a, b) => (a.key === "softphone" ? -1 : b.key === "softphone" ? 1 : a.key.localeCompare(b.key)));

  ordered.forEach((entry, i) => {
    const p = clamp(entry.pos || { x: rightEdge, y: bottomEdge });
    let x = p.x;
    let y = p.y;

    for (let attempt = 0; attempt < ordered.length + 2; attempt += 1) {
      const hit = ordered.slice(0, i).map((e) => result.get(e.key)).find((q) => (
        q && Math.abs(q.x - x) < FAB_SIZE + FAB_GAP && Math.abs(q.y - y) < FAB_SIZE + FAB_GAP
      ));
      if (!hit) break;

      // 同一列时优先上下错开，保留用户习惯的右下纵向排列。
      const above = hit.y - FAB_SIZE - FAB_GAP;
      const below = hit.y + FAB_SIZE + FAB_GAP;
      if (above >= EDGE) y = above;
      else if (below <= bottomEdge) y = below;
      else {
        // 纵向空间不够（手机键盘弹出等）时改为横向错开，保证两个按钮都完整可见。
        const left = hit.x - FAB_SIZE - FAB_GAP;
        const right = hit.x + FAB_SIZE + FAB_GAP;
        if (left >= EDGE) x = left;
        else if (right <= rightEdge) x = right;
        else y = EDGE;
      }
    }

    result.set(entry.key, clamp({ x, y }));
  });

  return result;
};

export function useFloatingFab(key, { defaultPos, onTap, panelOpen, legacyHiddenKey, avoid = [] }) {
  const [pos, setPos] = useState(null);
  const [dock, setDock] = useState(() => load(`${key}_dock`, "") || (legacyHiddenKey && localStorage.getItem(legacyHiddenKey) === "1" ? "right" : ""));
  const [peek, setPeek] = useState(false);
  const posRef = useRef(null);
  const dockRef = useRef(dock);
  const tapRef = useRef(onTap);
  const drag = useRef({ on: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const timer = useRef(null);
  tapRef.current = onTap;
  dockRef.current = dock;

  /**
   * 当前 hook 的坐标提交函数：先广播给另一个悬浮球，再统一写入共享位置表。
   * 事件只携带 key，不携带 React 对象，保证两个独立组件在页面任意位置挂载时都能同步。
   */
  const publish = useCallback((p) => {
    const q = clamp(p);
    posRef.current = q;
    setPos(q);
    save(posKey(key), toRelative(q));
    try { window.dispatchEvent(new CustomEvent(VIEWPORT_EVENT, { detail: { key } })); } catch { /* ignore */ }
  }, [key]);

  useEffect(() => {
    const raw = fromSaved(load(posKey(key), null)) || fromSaved(load(`${key}_pos`, null)) || defaultPos();
    const initial = clamp(raw);
    posRef.current = initial;
    setPos(initial);
    save(posKey(key), toRelative(initial));

    const sync = () => {
      if (drag.current.on) return;
      const current = posRef.current;
      if (!current) return;
      const { W, H } = viewport();
      if (!Number.isFinite(W) || !Number.isFinite(H) || W <= FAB_SIZE || H <= FAB_SIZE) return;
      // 必须用上次保存的「右下相对偏移」恢复；若用旧像素坐标对新视口重新算偏移，
      // 缩小窗口时旧 x 会大于新宽度，被误判成贴左并把两个球挤到同一列。
      const p = clamp(fromSaved(load(posKey(key), null)) || current);
      const entries = [
        { key, pos: p },
        ...avoid.map((k) => ({ key: k, pos: readPos(k, null) })).filter((e) => e.pos),
      ];
      const next = resolvePositions(entries).get(key) || p;
      const old = posRef.current;
      if (Math.abs(old.x - next.x) < 0.5 && Math.abs(old.y - next.y) < 0.5) return;
      posRef.current = next;
      setPos(next);
      save(posKey(key), toRelative(next));
    };

    const onResize = () => { if (!drag.current.on) sync(); };
    const onPeer = (e) => {
      if (e?.detail?.key === key) return;
      if (drag.current.on) return;
      sync();
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    window.addEventListener(VIEWPORT_EVENT, onPeer);
    window.visualViewport?.addEventListener("resize", onResize);
    // 首次挂载后等另一个悬浮球完成挂载，再统一检查间距；单次 rAF 可能早于第二个 React effect。
    const settle = window.setTimeout(() => {
      if (!drag.current.on) sync();
    }, 0);
    return () => {
      window.clearTimeout(settle);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      window.removeEventListener(VIEWPORT_EVENT, onPeer);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [key]); // eslint-disable-line

  /* 贴边时若同侧已有别的悬浮球（avoid 里的 key）挡在同一高度，则错开到它下方（放不下则上方），避免两个把手叠在一起 */
  const setDockSide = useCallback((side) => {
    dockRef.current = side; setDock(side); save(`${key}_dock`, side);
    if (!side || !posRef.current) return;
    const maxY = availableHeight() - FAB_SIZE - EDGE;
    let y = posRef.current.y;
    avoid.forEach((k) => {
      const o = readPos(k, null);
      if (load(`${k}_dock`, "") === side && o && Math.abs(o.y - y) < FAB_SIZE + FAB_GAP) y = o.y + FAB_SIZE + FAB_GAP <= maxY ? o.y + FAB_SIZE + FAB_GAP : o.y - FAB_SIZE - FAB_GAP;
    });
    if (y !== posRef.current.y) { const p = clamp({ x: posRef.current.x, y }); posRef.current = p; setPos(p); save(posKey(key), toRelative(p)); }
  }, [key]); // eslint-disable-line
  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const retract = () => { if (!drag.current.on) setPeek(false); };
  const retractSoon = useCallback((ms = RETRACT_MS) => { clear(); timer.current = setTimeout(retract, ms); }, []);
  /* auto=true（点击把手 / 触屏）→ 5 秒后自动缩回；hover → 若鼠标随后没进入按钮则 1.2 秒后缩回 */
  const showPeek = useCallback((auto = false) => { setPeek(true); retractSoon(auto ? TOUCH_PEEK_MS : HOVER_GRACE_MS); }, [retractSoon]);
  useEffect(() => { if (!panelOpen && dockRef.current) retractSoon(); }, [panelOpen, retractSoon]);

  const onPointerMove = useCallback((e) => {
    const d = drag.current; if (!d.on) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) d.moved = true;
    const p = clamp({ x: d.ox + dx, y: d.oy + dy });
    posRef.current = p; setPos(p);
    if (d.moved && dockRef.current && !sideOf(p)) setDockSide("");
  }, [setDockSide]);
  const onPointerUp = useCallback(() => {
    const d = drag.current; d.on = false;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    if (!d.moved) { tapRef.current?.(); return; }
    save(posKey(key), toRelative(posRef.current));
    const side = sideOf(posRef.current);
    setDockSide(side);
    try { window.dispatchEvent(new CustomEvent(VIEWPORT_EVENT, { detail: { key } })); } catch { /* ignore */ }
    if (side) { setPeek(true); retractSoon(); }
  }, [key, onPointerMove, retractSoon, setDockSide]);
  const onPointerDown = useCallback((e) => {
    if (!posRef.current) return;
    clear();
    const d = drag.current;
    d.on = true; d.moved = false; d.sx = e.clientX; d.sy = e.clientY;
    d.ox = dockRef.current ? edgeX(dockRef.current) : posRef.current.x; d.oy = posRef.current.y;
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }, [onPointerMove, onPointerUp]);

  const hide = useCallback(() => { setPeek(false); setDockSide(posRef.current && posRef.current.x < window.innerWidth / 2 ? "left" : "right"); }, [setDockSide]);
  /* 重置：回到默认位置并取消贴边 */
  const reset = useCallback(() => { clear(); publish(defaultPos()); setPeek(false); setDockSide(""); }, [publish, setDockSide]); // eslint-disable-line
  /* 贴边时按钮与把手同高：鼠标移到把手，按钮正好出现在鼠标下方 */
  const fabPos = pos && (dock ? { x: edgeX(dock), y: pos.y } : pos);
  const fabVisible = !!pos && (!dock || peek || !!panelOpen);
  const tabTop = fabPos ? fabPos.y + 4 : 0;
  return { fabPos, dock, fabVisible, tabVisible: !!pos && !!dock && !fabVisible, onPointerDown, hide, reset, showPeek, retractSoon, cancelRetract: clear, tabTop };
}

/* 面板位置：优先在按钮上方，放不下则下方；水平贴齐按钮并限制在视口内 */
export function panelStyleFor(fabPos, PW, PH) {
  if (!fabPos) return {};
  const W = window.innerWidth, H = window.innerHeight;
  let top = fabPos.y - PH - 8;
  if (top < 8) top = Math.min(fabPos.y + FAB_SIZE + 8, H - PH - 8);
  if (top < 8) top = 8;
  const left = Math.min(Math.max(8, fabPos.x + FAB_SIZE - PW), W - PW - 8);
  return { left, top };
}
